import React, { useState, useEffect } from "react";
import { profileService } from "../../services/api";
import { Client } from "../../types";
import { useAuth } from "../../context/AuthContext";
import { useNotification } from "../../context/NotificationContext";
import LoadingSpinner from "../../components/LoadingSpinner";

const Profile: React.FC = () => {
    const { user, updateUser } = useAuth();
    const notification = useNotification();
    const [profile, setProfile] = useState<Client | null>(null);
    const [newUsername, setNewUsername] = useState("");
    const [isEditingUsername, setIsEditingUsername] = useState(false);
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        loadProfile();
    }, []);

    const loadProfile = async () => {
        try {
            setIsLoading(true);
            const data = await profileService.get();
            setProfile(data);
            setNewUsername(data.username);
        } catch (err) {
            notification.error("Erreur lors du chargement du profil");
        } finally {
            setIsLoading(false);
        }
    };

    const handleUsernameChange = async (e: React.FormEvent) => {
        e.preventDefault();

        if (newUsername.length < 3) {
            notification.error(
                "Le nom d'utilisateur doit contenir au moins 3 caracteres",
            );
            return;
        }

        if (newUsername === profile?.username) {
            setIsEditingUsername(false);
            return;
        }

        setIsSaving(true);
        try {
            const updatedClient =
                await profileService.updateUsername(newUsername);
            setProfile(updatedClient);
            if (updateUser) {
                updateUser({ ...user!, username: newUsername });
            }
            notification.success("Nom d'utilisateur mis a jour avec succes");
            setIsEditingUsername(false);
        } catch (err: any) {
            notification.error(
                err.response?.data?.error || "Erreur lors de la mise a jour",
            );
        } finally {
            setIsSaving(false);
        }
    };

    const handlePasswordChange = async (e: React.FormEvent) => {
        e.preventDefault();

        if (newPassword !== confirmPassword) {
            notification.error("Les mots de passe ne correspondent pas");
            return;
        }

        if (newPassword.length < 6) {
            notification.error(
                "Le mot de passe doit contenir au moins 6 caracteres",
            );
            return;
        }

        setIsSaving(true);
        try {
            await profileService.updatePassword(newPassword);
            notification.success("Mot de passe mis a jour avec succes");
            setNewPassword("");
            setConfirmPassword("");
        } catch (err: any) {
            notification.error(
                err.response?.data?.error || "Erreur lors de la mise a jour",
            );
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <LoadingSpinner size="lg" text="Chargement du profil..." />
            </div>
        );
    }

    return (
        <div className="min-h-screen py-8 px-4 relative">
            {/* Background image */}
            <div className="fixed inset-0 overflow-hidden">
                <div style={{
                    position: "absolute", inset: 0,
                    backgroundImage: "url('/bg.png')",
                    backgroundSize: "cover", backgroundPosition: "center",
                }}></div>
            </div>
            {/* Background overlay with opacity */}
            <div className="fixed inset-0 bg-primary-dark/85"></div>

            <div className="max-w-2xl mx-auto relative z-10">
                <h1 className="text-3xl font-bold text-white mb-8">
                    Mon Profil
                </h1>

                {/* Profile Info */}
                <div className="card p-6 mb-8">
                    <h2 className="text-xl font-semibold text-white mb-4">
                        Informations du compte
                    </h2>

                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm text-gray-400 mb-1">
                                Nom d'utilisateur
                            </label>
                            {isEditingUsername ? (
                                <form
                                    onSubmit={handleUsernameChange}
                                    className="flex gap-2"
                                >
                                    <input
                                        type="text"
                                        value={newUsername}
                                        onChange={(e) =>
                                            setNewUsername(e.target.value)
                                        }
                                        className="input-field flex-1"
                                        placeholder="Nouveau nom d'utilisateur"
                                        minLength={3}
                                        autoFocus
                                    />
                                    <button
                                        type="submit"
                                        disabled={isSaving}
                                        className="btn-primary py-2 px-4 disabled:opacity-50"
                                    >
                                        {isSaving ? "..." : "OK"}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setIsEditingUsername(false);
                                            setNewUsername(
                                                profile?.username || "",
                                            );
                                        }}
                                        className="btn-secondary py-2 px-4"
                                    >
                                        Annuler
                                    </button>
                                </form>
                            ) : (
                                <div className="flex items-center gap-3">
                                    <p className="text-white text-lg">
                                        {user?.username || profile?.username}
                                    </p>
                                    <button
                                        onClick={() =>
                                            setIsEditingUsername(true)
                                        }
                                        className="text-accent hover:text-accent-light text-sm transition-colors"
                                    >
                                        Modifier
                                    </button>
                                </div>
                            )}
                        </div>

                        <div>
                            <label className="block text-sm text-gray-400 mb-1">
                                Role
                            </label>
                            <p className="text-white">
                                <span className="inline-block bg-accent/20 text-accent text-sm px-3 py-1 rounded-full">
                                    {user?.role || profile?.role}
                                </span>
                            </p>
                        </div>

                        {profile?.created_at && (
                            <div>
                                <label className="block text-sm text-gray-400 mb-1">
                                    Membre depuis
                                </label>
                                <p className="text-white">
                                    {new Date(
                                        profile.created_at,
                                    ).toLocaleDateString("fr-FR", {
                                        year: "numeric",
                                        month: "long",
                                        day: "numeric",
                                    })}
                                </p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Change Password */}
                <div className="card p-6">
                    <h2 className="text-xl font-semibold text-white mb-4">
                        Changer le mot de passe
                    </h2>

                    <form onSubmit={handlePasswordChange} className="space-y-4">
                        <div>
                            <label
                                htmlFor="newPassword"
                                className="block text-sm font-medium text-gray-300 mb-2"
                            >
                                Nouveau mot de passe
                            </label>
                            <input
                                id="newPassword"
                                type="password"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                className="input-field"
                                placeholder="Minimum 6 caracteres"
                                minLength={6}
                            />
                        </div>

                        <div>
                            <label
                                htmlFor="confirmPassword"
                                className="block text-sm font-medium text-gray-300 mb-2"
                            >
                                Confirmer le mot de passe
                            </label>
                            <input
                                id="confirmPassword"
                                type="password"
                                value={confirmPassword}
                                onChange={(e) =>
                                    setConfirmPassword(e.target.value)
                                }
                                className="input-field"
                                placeholder="Confirmez le mot de passe"
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={
                                isSaving || !newPassword || !confirmPassword
                            }
                            className="btn-primary py-2 px-6 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isSaving ? "Mise a jour..." : "Mettre a jour"}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default Profile;
